<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <!-- Loading -->
        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status"></div>
          <div class="mt-2 text-muted small">Loading event details…</div>
        </div>

        <!-- Content -->
        <div v-else class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded">
          <!-- Carousel -->
          <div class="hero my-3" v-if="mediaSlides.length">
          <div class="slide-frame" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
            <div
              v-if="useContain && currentSlideBgSrc && currentSlideMedia && currentSlideMedia.type === 'image'"
              class="slide-bg-blur"
              :style="{ backgroundImage: `url(${currentSlideBgSrc})` }"
            />

            <img
              v-if="currentSlideMedia && currentSlideMedia.type === 'image'"
              :key="`slide-img-${currentSlide}`"
              :src="currentSlideMedia.src"
              class="slide-img"
              :class="useContain ? 'fit-contain' : 'fit-cover'"
              :alt="`Slide ${currentSlide + 1}`"
              @load="onImgLoad"
            />
            <template v-else-if="currentSlideMedia && currentSlideMedia.type === 'video'">
              <div
                v-if="!isCurrentVideoPlaying"
                class="video-preview-shell"
                @click="startVideoPlayback"
              >
                <img
                  :src="currentSlideMedia.thumb || VIDEO_THUMB_PLACEHOLDER"
                  class="slide-img fit-cover"
                  :alt="`Video preview ${currentSlide + 1}`"
                />
                <button type="button" class="video-preview-play" aria-label="Play video">
                  <i class="fas fa-play"></i>
                </button>
              </div>
              <template v-if="isCurrentVideoPlaying">
                <vue-plyr
                  v-if="currentSlideMedia.videoKind === 'url'"
                  :key="`slide-player-file-${currentSlide}-${currentSlideMedia.src}`"
                  class="slide-player"
                  :options="plyrOptions"
                >
                  <video
                    controls
                    playsinline
                    preload="metadata"
                    disablePictureInPicture
                    :poster="currentSlideMedia.thumb || ''"
                  >
                    <source :src="currentSlideMedia.src" />
                  </video>
                </vue-plyr>
                <vue-plyr
                  v-else-if="currentSlideMedia.videoKind === 'youtube'"
                  :key="`slide-player-yt-${currentSlide}-${currentSlideMedia.youtubeId}`"
                  class="slide-player"
                  :options="plyrOptions"
                >
                  <div class="plyr__video-embed" :data-poster="currentSlideMedia.thumb || ''">
                    <iframe
                      :src="`https://www.youtube.com/embed/${currentSlideMedia.youtubeId}?playsinline=1&modestbranding=1&rel=0&enablejsapi=1`"
                      allowfullscreen
                      allowtransparency
                      allow="autoplay"
                    ></iframe>
                  </div>
                </vue-plyr>
                <div
                  v-else
                  :key="`slide-player-iframe-${currentSlide}-${currentSlideMedia.embedUrl}`"
                  class="slide-iframe-wrap"
                >
                  <iframe
                    class="slide-iframe"
                    :src="currentSlideMedia.embedUrl || currentSlideMedia.src"
                    title="Video player"
                    loading="lazy"
                    allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                    allowfullscreen
                  ></iframe>
                </div>
              </template>
            </template>

            <button v-if="mediaSlides.length > 1" class="nav prev" @click="prevSlide" aria-label="Previous">
              <i class="fas fa-chevron-left"></i>
            </button>
            <button v-if="mediaSlides.length > 1" class="nav next" @click="nextSlide" aria-label="Next">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

            <div class="thumbs mt-2" v-if="mediaSlides.length > 1">
              <button
                v-for="(item, i) in mediaSlides"
                :key="`thumb-${i}-${item.type}-${item.src}`"
                type="button"
                class="thumb-btn"
                :class="{ active: i === currentSlide }"
                @mousedown.prevent
                @click.prevent="goToSlide(i)"
                :title="`Go to slide ${i+1}`"
              >
                <img v-no-right-click :src="item.thumb || item.src" class="thumb" alt="thumbnail" />
                <span v-if="item.type === 'video'" class="thumb-play" aria-hidden="true">
                  <i class="fas fa-play"></i>
                </span>
              </button>
            </div>
          </div>

          <!-- Title -->
          <h5 class="mb-2">{{ title || 'Event' }}</h5>

          <!-- About -->
          <div class="mb-3" v-if="info.description">
            <div class="section-title">About The Event</div>
            <p class="mb-0">{{ info.description }}</p>
          </div>

          <!-- Event details -->
          <div class="mb-3">
            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📅</span>
              <div>{{ eventDateRange }}</div>
            </div>

            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📍</span>
              <div class="flex-grow-1 text-dark">
                {{ info.fulladdress || '—' }}
              </div>
              <a
                v-if="hasMap"
                :href="mapHref"
                target="_blank"
                rel="noopener"
                class="ml-2 link-arrow"
                title="Open in Maps"
              >
                <span aria-hidden="true"><i class="fas fa-location-arrow"></i></span>
              </a>
            </div>

            <div v-if="hasMap" class="map-card mt-3">
              <div class="map-card__header">
                <span><i class="fas fa-map-marked-alt mr-2"></i>Event Location</span>
                <a :href="mapHref" target="_blank" rel="noopener">Open in Google Maps</a>
              </div>
              <div class="map-frame">
                <iframe
                  class="map-frame__iframe"
                  :src="mapEmbedSrc"
                  loading="lazy"
                  allowfullscreen
                  referrerpolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>

          <div class="mb-3 text-small">
            Host: <strong>{{ info.hostname || '-' }}</strong>
          </div>

          <div class="mb-3">
            <div class="section-title">Add to Calendar</div>
            <AddToCalendar
              :title="title"
              :description="info.description || ''"
              :location="info.address || ''"
              :start="eventStart"
              :end="eventEnd"
              :url="eventUrl"
            />
          </div>

          <hr />

          <!-- RSVP -->
          <PublicRsvp
            :event-id="eventId"
            :raw-event-id="rawEventIdComputed"
            :is-logged-in="isLoggedIn"
            :session-user-id="session.userId"
            :session-email="session.email"
            :session-phone="session.phone"
            :initial-contact="activeContact"
            :existing-rsvp="existingRsvpForChild"
            :rsvp-option="rsvpOption"
            :rsvp-family="rsvpFamily"
            @need-profile="ensureSessionProfile"
            @rsvp-updated="handleRsvpUpdated"
            @contact-updated="handleContactUpdated"
            @invitee-resolved="handleInviteeResolved"
          />

          <hr />

          <!-- Albums -->
          <div v-if="hasAlbumAccess" class="mb-2">
            <div class="section-title">Albums</div>
            <p class="text-small text-muted mb-2">
              When photos are shared for this event, you will be able to view them here.
            </p>

            <div v-if="isHost" class="mb-4">
              <FilePondUploader
                :eventid="eventId"
                :userid="session.userId"
                @upload-complete="reloadAlbumsAfterUpload"
              />
            </div>

            <div class="album-images-section">
              <div v-if="albumImagesLoading && albumImages.length===0" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-primary"></div>
                <div class="mt-2 text-muted small">Loading images...</div>
              </div>

              <AlbumImagesGrid
                v-else
                :images="albumImages"
                :is-host="isHost"
                :eventid="eventId"
                :userid="session.userId"
                @image-deleted="reloadAlbumsAfterDelete"
                @image-download="downloadImage"
                @load-more="loadAlbumImages(true)"
              />

              <div v-if="albumHasMore" class="text-center py-3">
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="loadAlbumImages(true)"
                  :disabled="albumImagesLoading"
                >
                  <span v-if="albumImagesLoading" class="spinner-border spinner-border-sm mr-2"></span>
                  Load more
                </button>
              </div>
            </div>
          </div>

          <div v-else class="mb-2">
            <div class="section-title">Albums</div>
            <div class="alert alert-info">
              <i class="fas fa-info-circle mr-2"></i>
              Album access is not available for this event.
            </div>
          </div>
        </div>
      </div>

      <!-- Right column: Analytics (host only) -->
      <div class="col-md-4" v-if="isHost && eventRow.stats">
        <EventAnalytics :stats="eventRow.stats" class="mt-3" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import FilePondUploader from '@/components/public/FilePondUploader.vue'
import AlbumImagesGrid from '@/components/public/AlbumImagesGrid.vue'
import PublicRsvp from '@/components/public/PublicRsvp.vue'
import EventAnalytics from '@/components/public/EventAnalytics.vue'
import AddToCalendar from '@/components/common/AddToCalendar.vue'
import { useHead } from '@vueuse/head'
import { formatEventDateRange } from '@/utils/date'

const DOWNLOAD_PRESETS = {
  low:   { key: 'low', label: 'Low', maxSide: 800, quality: 0.6 },
  medium:{ key: 'medium', label: 'Medium', maxSide: 1600, quality: 0.8 },
  high:  { key: 'high', label: 'High', maxSide: null, quality: 0.95 },
}
/* ---------- route / ids ---------- */
const route = useRoute() 
const session = useSessionStore()



/* base64url helpers */
function b64urlDecode(input) {
  if (!input) return null
  try {
    const s = String(input).replace(/-/g, '+').replace(/_/g, '/')
    const pad = s.length % 4 ? 4 - (s.length % 4) : 0
    const padded = s + '='.repeat(pad)
    return decodeURIComponent(escape(atob(padded)))
  } catch {
    try { return atob(input) } catch { return null }
  }
}
function b64urlEncode(str) {
  try {
    return btoa(String(str)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  } catch { return '' }
}
function isEmail(val) {
  return /\S+@\S+\.\S+/.test(String(val || '').trim())
}
function normalizePhone10(val) {
  const digits = String(val || '').replace(/\D/g, '')
  if (!digits) return ''
  return digits.length > 10 ? digits.slice(-10) : digits
}
function buildDateTime(date, time) {
  if (!date) return null
  const t = time && String(time).trim() ? time : '00:00'
  const iso = `${date}T${t}`
  const d = new Date(iso)
  return isNaN(d) ? null : d
}
function deriveEventIdAndToken(r) {
  const path = (r.path || r.fullPath || '').split('?')[0]
  const segs = path.split('/').filter(Boolean)
  let eidEnc = r.params?.eventid ? String(r.params.eventid) : null
  let tokenEnc = r.params?.token ? String(r.params.token) : null
  if (segs[0] === 'i') {
    if (!eidEnc && segs[1]) eidEnc = segs[1]
    if (!tokenEnc && segs[2]) tokenEnc = segs[2]
  }
  const eidDec = b64urlDecode(eidEnc)
  const tokDec = b64urlDecode(tokenEnc)
  const tokenNormalized = tokDec ? (isEmail(tokDec) ? tokDec : normalizePhone10(tokDec)) : ''
  return { rawEventId: eidEnc || null, eventId: /^\d+$/.test(eidDec || '') ? eidDec : null, token: tokenNormalized || '' }
}
const firstParse = deriveEventIdAndToken(route)
const eventId = ref(firstParse.eventId)
const rawEventId = ref(firstParse.rawEventId)
const tokenEmailOrPhone = ref(firstParse.token || '')

watch(() => route.fullPath, () => {
  const parsed = deriveEventIdAndToken(route)
  eventId.value = parsed.eventId
  rawEventId.value = parsed.rawEventId
  tokenEmailOrPhone.value = parsed.token || ''
})

const rawEventIdComputed = computed(() => {
  return rawEventId.value || (eventId.value ? b64urlEncode(eventId.value) : '')
})

/* ---------- state ---------- */
const loading = ref(true)
const info = ref({})
const title = computed(() => info.value.orgname || info.value.eventname || 'Event')
const eventRow = ref({}) // summary + stats

/* ---------- formatting ---------- */
const eventDateRange = computed(() => {
  const text = formatEventDateRange({
    fromDate: info.value.fromdate,
    toDate: info.value.todate,
    fromTime: info.value.fromtime,
    toTime: info.value.totime
  })
  return text || ''
})
const eventStart = computed(() => buildDateTime(info.value.fromdate, info.value.fromtime))
const eventEnd = computed(() => buildDateTime(info.value.todate || info.value.fromdate, info.value.totime || info.value.fromtime))
const hasMap = computed(() => Boolean((info.value.lat || info.value.latitude) && (info.value.lng || info.value.longitude)))
const mapHref = computed(() => {
  if (!hasMap.value) return '#'
  const lat = info.value.lat || info.value.latitude
  const lng = info.value.lng || info.value.longitude
  return `https://www.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}`
})
const mapEmbedSrc = computed(() => {
  if (!hasMap.value) return ''
  const lat = info.value.lat || info.value.latitude
  const lng = info.value.lng || info.value.longitude
  return `https://www.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}&hl=en&output=embed`
})

/* ---------- media / carousel ---------- */
const plyrOptions = {
  controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
  youtube: { noCookie: true, rel: 0, modestbranding: 1 },
  autoplay: true
}
const parseYouTubeId = (url) => {
  if (!url) return ''
  const s = String(url).trim()
  const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i)
  return m?.[1] || ''
}
const isDirectVideoUrl = (url) => {
  const s = String(url || '').trim().toLowerCase().split('?')[0].split('#')[0]
  return /\.(mp4|mov|webm|m3u8|ogg|ogv)$/i.test(s)
}
const isCanvaWatchUrl = (url) => {
  const s = String(url || '').trim().toLowerCase()
  return s.includes('canva.com') && s.includes('/watch')
}
const withAutoplayParam = (url) => {
  if (!url) return ''
  try {
    const u = new URL(url)
    u.searchParams.set('autoplay', '1')
    return u.toString()
  } catch (_) {
    return url
  }
}
const toCanvaEmbedUrl = (url) => {
  const s = String(url || '').trim()
  if (!s) return ''
  try {
    const u = new URL(s)
    if (u.hostname.includes('canva.com') && u.pathname.includes('/watch')) {
      u.pathname = u.pathname.replace('/watch', '/view')
      u.searchParams.set('embed', '1')
      u.searchParams.set('autoplay', '1')
      return u.toString()
    }
  } catch (_) {
    return s
  }
  return s
}
const VIDEO_THUMB_PLACEHOLDER = 'https://dummyimage.com/320x180/222/fff.png&text=Video'
const mediaSlides = computed(() => {
  const slides = []
  const photosNew = Array.isArray(info.value.photos_new) ? info.value.photos_new : []
  const photoNewVideoPreview = photosNew
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return ''
      const type = String(entry.type || '').trim().toLowerCase()
      const value = String(entry.value || '').trim()
      return type === 'video' ? value : ''
    })
    .find(Boolean) || ''

  const vLink = String(info.value.video_link || eventRow.value?.video_link || '').trim()
  const baseVideoThumb = String(
    photoNewVideoPreview ||
    info.value.video_thumbnail ||
    info.value.video_thumb ||
    eventRow.value?.video_thumbnail ||
    eventRow.value?.video_thumb ||
    ''
  ).trim()

  const pushImage = (v) => {
    const src = String(v || '').trim()
    if (!src) return
    slides.push({ type: 'image', src, thumb: src })
  }
  const pushVideo = (thumbCandidate = '') => {
    if (!vLink) return false
    const yid = parseYouTubeId(vLink)
    const canvaEmbedUrl = isCanvaWatchUrl(vLink) ? toCanvaEmbedUrl(vLink) : ''
    const isFileUrl = isDirectVideoUrl(vLink)
    const isIframeSource = !yid && !isFileUrl
    const thumb = String(thumbCandidate || baseVideoThumb || VIDEO_THUMB_PLACEHOLDER).trim()
    slides.push({
      type: 'video',
      src: vLink,
      thumb,
      videoKind: yid ? 'youtube' : (isIframeSource ? 'iframe' : 'url'),
      youtubeId: yid || '',
      embedUrl: yid
        ? `https://www.youtube.com/embed/${yid}`
        : (canvaEmbedUrl ? canvaEmbedUrl : withAutoplayParam(vLink))
    })
    return true
  }

  if (photosNew.length) {
    let hasVideoEntry = false
    photosNew.forEach((entry) => {
      if (entry && typeof entry === 'object') {
        const type = String(entry.type || '').trim().toLowerCase()
        const value = String(entry.value || '').trim()
        if (type === 'video') {
          hasVideoEntry = true
          if (!pushVideo(value) && value) pushImage(value)
          return
        }
        if (value) pushImage(value)
        return
      }
      pushImage(entry)
    })
    if (!hasVideoEntry && vLink) pushVideo(baseVideoThumb)
  } else if (Array.isArray(info.value.photos) && info.value.photos.length) {
    info.value.photos.forEach(pushImage)
    if (vLink) pushVideo(baseVideoThumb)
  }
  if (!slides.length) {
    pushImage(info.value.type_image || info.value.banner || info.value.cover)
    if (vLink) pushVideo(baseVideoThumb)
    else if (baseVideoThumb) pushImage(baseVideoThumb)
  }

  const out = []
  const seen = new Set()
  for (const item of slides) {
    const key = `${item.type}:${item.src}`
    if (!item.src || seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
})
const resolvePreviewAbsolute = (value) => {
  if (typeof window === 'undefined') return ''
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  try {
    const url = new URL(trimmed, window.location.origin)
    if (window.location.protocol === 'https:' && url.protocol === 'http:') url.protocol = 'https:'
    return url.toString()
  } catch {
    if (/^\/\//.test(trimmed)) {
      return `${window.location.protocol}${trimmed}`
    }
    if (/^\//.test(trimmed)) {
      return `${window.location.origin}${trimmed}`
    }
    return ''
  }
}
const currentSlide = ref(0)
const currentSlideMedia = computed(() => mediaSlides.value[currentSlide.value] || null)
const currentSlideBgSrc = computed(() => currentSlideMedia.value?.thumb || currentSlideMedia.value?.src || '')
const videoPlaybackSlide = ref(null)
const isCurrentVideoPlaying = computed(() => videoPlaybackSlide.value === currentSlide.value)
let autoTimer = null
const AUTOPLAY_MS = 4500
const nextSlide = () => { const n = mediaSlides.value.length; if (n) currentSlide.value = (currentSlide.value + 1) % n }
const prevSlide = () => { const n = mediaSlides.value.length; if (n) currentSlide.value = (currentSlide.value - 1 + n) % n }
const goToSlide = (i) => { const n = mediaSlides.value.length; if (n) currentSlide.value = Math.max(0, Math.min(i, n - 1)) }
const stopAuto = () => { if (autoTimer) { clearInterval(autoTimer); autoTimer = null } }
const startAuto = () => { stopAuto(); if (mediaSlides.value.length > 1) autoTimer = setInterval(nextSlide, AUTOPLAY_MS) }
const pauseAuto = stopAuto
const resumeAuto = () => {
  if (mediaSlides.value.length > 1 && currentSlideMedia.value?.type !== 'video' && !isCurrentVideoPlaying.value) startAuto()
}
const startVideoPlayback = () => {
  videoPlaybackSlide.value = currentSlide.value
  stopAuto()
}

/* Portrait containment */
const useContain = ref(false)
const onImgLoad = (e) => {
  const img = e?.target
  if (!img || !img.naturalWidth || !img.naturalHeight) { useContain.value = false; return }
  const ar = img.naturalWidth / img.naturalHeight
  useContain.value = ar < 1
}

/* ---------- Dynamic meta tags ---------- */
const pageTitle = computed(() => {
  const base = info.value.eventname || info.value.orgname || 'Event'
  return `${base} | Invi8`
})
const pageDescription = computed(() => {
  const desc = (info.value.description || '').trim()
  if (desc) return desc
  const name = info.value.eventname || info.value.orgname
  return name ? `View details for ${name} on Invi8.` : 'Discover event details, RSVP options, and shared albums on Invi8.'
})
const pageKeywords = computed(() => {
  const tokens = [
    info.value.eventname,
    info.value.orgname,
    info.value.hostname,
    info.value.eventtype,
    info.value.city,
    'Invi8',
    'event invitation'
  ].filter(Boolean)
  return tokens.join(', ')
})
const canonicalUrl = computed(() => {
  if (typeof window === 'undefined') return ''
  try {
    return new URL(route.fullPath || '/', window.location.origin).toString()
  } catch {
    return window.location.href || ''
  }
})
const previewImageAbsolute = computed(() => {
  const raw = mediaSlides.value[0]?.thumb || mediaSlides.value[0]?.src || info.value.banner || info.value.cover || ''
  return resolvePreviewAbsolute(raw)
})
const eventUrl = computed(() => {
  if (typeof window === 'undefined') return ''
  return window.location.href
})

useHead(() => {
  const metaEntries = [
    { name: 'description', content: pageDescription.value },
    { name: 'keywords', content: pageKeywords.value || 'invi8, event, invitation' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:description', content: pageDescription.value },
    previewImageAbsolute.value ? { property: 'og:image', content: previewImageAbsolute.value } : null,
    { property: 'og:url', content: canonicalUrl.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:title', content: pageTitle.value },
    { property: 'twitter:description', content: pageDescription.value },
    previewImageAbsolute.value ? { property: 'twitter:image', content: previewImageAbsolute.value } : null,
  ].filter(Boolean)

  const links = canonicalUrl.value
    ? [{ rel: 'canonical', href: canonicalUrl.value }]
    : []

  return {
    title: pageTitle.value,
    meta: metaEntries,
    link: links
  }
})

watch(mediaSlides, (arr) => {
  if (!arr.length) { currentSlide.value = 0; stopAuto(); return }
  if (currentSlide.value >= arr.length) currentSlide.value = 0
  if (arr.length > 1 && !autoTimer) startAuto()
})
watch(currentSlide, () => { videoPlaybackSlide.value = null })
watch(currentSlideMedia, (media) => {
  if (!media) return
  if (media.type === 'video' || isCurrentVideoPlaying.value) {
    stopAuto()
    return
  }
  if (mediaSlides.value.length > 1 && !autoTimer) startAuto()
})

/* ---------- Host & Album Access ---------- */
const isHost = ref(false)
const hasAlbumAccess = ref(false)

/* Album pagination */
const albumImages = ref([])
const albumImagesLoading = ref(false)
const albumPage = ref(1)
const albumHasMore = ref(false)

const isLoggedIn = computed(() => !!session.userId)
const activeContact = computed(() =>
  tokenEmailOrPhone.value || session.email || session.phone || ''
)
const rsvpOption = computed(() => Number(info.value.rsvp_option ?? eventRow.value.rsvp_option ?? 1) === 1 ? 1 : 0)
const rsvpFamily = computed(() => Number(info.value.rsvp_family ?? eventRow.value.rsvp_family ?? 1) === 1 ? 1 : 0)

/* existing rsvp object passed to child */
const existingRsvpForChild = computed(() => ({
  status: Number(eventRow.value.status || 0),
  adult: Number(eventRow.value.adult || 0),
  child: Number(eventRow.value.child || 0),
  note: eventRow.value.note || eventRow.value.notes || ''
}))

/* ---------- Invitee id (from checkExistingInvitation) ---------- */
const inviteeUserId = ref('')
const inviteeUserIdMain = ref('')

async function resolveInviteeId(contact) {
  inviteeUserId.value = '';
  inviteeUserIdMain.value = '';

  if (!eventId.value || !contact) return null;

  try {
    const normalizedContact = isEmail(contact) ? contact : normalizePhone10(contact)
    const fd = new FormData();
    fd.append('eventid', eventId.value);
    // API expects contact in the "email" field even when it's a phone number
    fd.append('email', normalizedContact || '');
    fd.append('name', ''); // pass name if you have it

    const res = await api.checkExistingInvitation(fd);
    const data = res?.data;
    const ok = data?.status === true || data?.status === 'ok';

    if (ok) {
      const id = data?.userdetails?.id || data?.id || '';
      const attendeeId = Array.isArray(data?.counts_data)
        ? data.counts_data[0]?.id || ''
        : data?.id || '';

      inviteeUserId.value = id ? String(id) : '';
      inviteeUserIdMain.value = attendeeId ? String(attendeeId) : '';

      // Return whichever you actually intend — or both if useful
      return {
        userId: inviteeUserId.value || null,
        attendeeId: inviteeUserIdMain.value || null,
      };
    }

    return null;
  } catch (e) {
    console.error('checkExistingInvitation failed', e);
    return null;
  }
}


/* ---------- Albums loaders ---------- */
async function loadAlbumImages(append = false) {
  if (!hasAlbumAccess.value || !eventId.value) { albumImages.value = []; return }
  try {
    albumImagesLoading.value = true
    const pageToLoad = append ? (albumPage.value + 1) : 1
    const fd = new FormData()
    fd.append('event_id', eventId.value)
    fd.append('page', String(pageToLoad))
    if (session.userId) fd.append('user_id', session.userId)
    const res = await api.getAlbumPublic(fd)
    const ok = res?.data?.status === true || res?.data?.status === 'ok' || res?.status === 'ok'
    const list = ok ? (res.data?.images || res.data?.data || res.data?.list || []) : []
    const items = Array.isArray(list) ? list : []
    albumHasMore.value = items.length > 0
    if (append) {
      albumPage.value = pageToLoad
      albumImages.value = [...albumImages.value, ...items]
    } else {
      albumPage.value = 1
      albumImages.value = items
    }
  } catch (e) {
    console.error('getAlbumPublic failed', e)
    if (!append) albumImages.value = []
  } finally {
    albumImagesLoading.value = false
  }
}
function reloadAlbumsAfterUpload () { loadAlbumImages(false) }
function reloadAlbumsAfterDelete () { loadAlbumImages(false) }
function onScroll () {
  if (!albumHasMore.value || albumImagesLoading.value) return
  const nearBottom = (window.innerHeight + window.scrollY) >= (document.body.offsetHeight - 200)
  if (nearBottom) loadAlbumImages(true)
}

/* ---------- Host & Album access ---------- */
const checkHostAndAlbumAccess = async () => {
  if (!eventId.value || !session.userId) { isHost.value = false; hasAlbumAccess.value = false; return }
  try {
    const fd = new FormData()
    fd.append('eventid', eventId.value)
    fd.append('userid', session.userId)
    const res = await api.checkISHost(fd)
    const payload = res?.data || res || {}
    const d = (payload.data || payload)
    isHost.value = String(d?.is_host || '').toLowerCase() === 'yes'
    hasAlbumAccess.value = String(d?.album_access || '').toLowerCase() === 'yes'
  } catch (e) {
    console.error('checkISHost failed', e)
    isHost.value = false
    hasAlbumAccess.value = false
  }
}

/* ---------- Profile ensure ---------- */
async function ensureSessionProfile() {
  try {
    if ((!session.email && !session.phone) && typeof session.fetchProfile === 'function') {
      const prof = await session.fetchProfile()
      if (prof?.email && !session.email) session.email = prof.email
      if (prof?.phone && !session.phone) session.phone = prof.phone
    }
  } catch (e) {
    console.error('fetchProfile failed', e)
  }
}

/* ---------- Invitee state & counts (now uses inviteeUserId) ---------- */
async function fetchWebCountByContact(contact) {
  if (!eventId.value || !contact) return
  try {
    const normalizedContact = isEmail(contact) ? contact : normalizePhone10(contact)
    // Ensure we have the invite id for this contact
    if (!inviteeUserId.value) {
      await resolveInviteeId(normalizedContact)
    }
    console.log(inviteeUserId.value);
    const fd = new FormData()
    fd.append('eventid', eventId.value)
    fd.append('userid', inviteeUserId.value || '')   // <-- IMPORTANT: use invitee id
    if (normalizedContact.includes('@')) fd.append('email', normalizedContact)
    else fd.append('email', normalizedContact)

    const res = await api.getWebCount(fd)
    if (res?.data?.status === 'ok') {
      const first = res.data.data?.[0] || {}
      eventRow.value.status = Number(first.status || 0)
      eventRow.value.adult  = Number(first.adult  || 0)
      eventRow.value.child  = Number(first.child  || 0)
      eventRow.value.note   = first.note || first.notes || ''
    }
  } catch (e) {
    console.error('getWebCount failed', e)
  }
}

async function refreshInviteeState() {
  const contact = activeContact.value
  if (!contact) return
  // always resolve first, then fetch counts
  await resolveInviteeId(contact)
  await fetchWebCountByContact(contact)
}

/* ---------- Detail loader ---------- */
async function loadDetail() {
  loading.value = true
  try {
    const fd = new FormData()
    fd.append('eventid', eventId.value)
    fd.append('userid', session.userId || '')
    const res = await api.getInvitationDetail(fd)
    const ok = res?.data?.status === 'ok'
    if (ok) {
      const data = res.data
      const infoData = data.eventsinfo?.[0] || {}
      info.value = infoData
      eventRow.value = {
        ...eventRow.value,
        ...infoData,
        stats: {
          total: Number(data.total_members_count) || 0,
          sent: Number(data.sent_members_count) || 0,
          pending: Number(data.notsent_members_count) || 0,
          yes: Number(data.yescount) || 0,
          no: Number(data.nocount) || 0,
          maybe: Number(data.maybecount) || 0,
          noresponse: Number(data.noresponsecount) || 0,
          yesAdult: Number(data.yesadult) || 0,
          yesChild: Number(data.yeschild) || 0,
          maybeAdult: Number(data.mayadult) || 0,
          maybeChild: Number(data.maychild) || 0,
        }
      }
      currentSlide.value = 0
      stopAuto()
      if (mediaSlides.value.length > 1) startAuto()
    } else {
      info.value = {}
      eventRow.value = {}
    }

    await checkHostAndAlbumAccess()
    if (hasAlbumAccess.value) await loadAlbumImages(false)

    await ensureSessionProfile()
    await refreshInviteeState()
  } catch (error) {
    console.error('Error loading event detail:', error)
  } finally {
    loading.value = false
  }
}

/* ---------- Mount / unmount ---------- */
onMounted(() => {
  loadDetail()
  window.addEventListener('scroll', onScroll, { passive: true })
})
onBeforeUnmount(() => {
  stopAuto()
  window.removeEventListener('scroll', onScroll)
})

/* ---------- Child events ---------- */
async function handleRsvpUpdated() {
  await refreshInviteeState()
}
async function handleContactUpdated(contact) {
  tokenEmailOrPhone.value = contact || tokenEmailOrPhone.value
  await resolveInviteeId(tokenEmailOrPhone.value)
  await refreshInviteeState()
}
function handleInviteeResolved(id) {
  inviteeUserId.value = String(id || '')
}

/* ---------- Download helper ---------- */
const resolveAbsoluteUrl = (value) => {
  if (!value) return ''
  const trimmed = String(value).trim()
  if (!trimmed) return ''

  try {
    const url = new URL(trimmed, window.location.origin)
    if (window.location.protocol === 'https:' && url.protocol === 'http:') {
      url.protocol = 'https:'
    }
    return url.href
  } catch {
    if (/^\/\//.test(trimmed)) {
      return `${window.location.protocol}${trimmed}`
    }
    if (window.location.protocol === 'https:' && trimmed.startsWith('http://')) {
      return trimmed.replace(/^http:/, 'https:')
    }
    return trimmed
  }
}

const normalizeDownloadPayload = (payload, fallbackName, fallbackPreset) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return {
      url: payload.url || payload.imageUrl || payload.image || '',
      name: payload.name || fallbackName,
      preset: payload.preset || fallbackPreset || 'high'
    }
  }

  return {
    url: typeof payload === 'string' ? payload : '',
    name: fallbackName,
    preset: fallbackPreset || 'high'
  }
}

const fetchImageBlob = async (resolvedUrl) => {
  const response = await fetch(resolvedUrl, {
    mode: 'cors',
    credentials: 'omit',
    referrerPolicy: 'no-referrer'
  })
  if (!response.ok) throw new Error(`Download failed with status ${response.status}`)
  return response.blob()
}

const resizeBlobForPreset = async (blob, preset) => {
  if (!preset || !preset.maxSide) return blob

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      const needsResize = Math.max(width, height) > preset.maxSide
      if (!needsResize) {
        URL.revokeObjectURL(objectUrl)
        resolve(blob)
        return
      }

      if (width >= height) {
        height = Math.round((preset.maxSide / width) * height)
        width = preset.maxSide
      } else {
        width = Math.round((preset.maxSide / height) * width)
        height = preset.maxSide
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (resized) => {
          URL.revokeObjectURL(objectUrl)
          if (resized) resolve(resized)
          else resolve(blob)
        },
        blob.type || 'image/jpeg',
        preset.quality ?? 0.85
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(blob)
    }
    img.src = objectUrl
  })
}

const buildFileName = (baseName, presetKey, mimeType) => {
  const defaultBase = `event-image-${eventId.value || 'invi8'}-${Date.now()}`
  const rawBase = (baseName || '').trim() || defaultBase
  const baseWithoutExt = rawBase.replace(/\.[^/.]+$/, '')
  const extFromName = rawBase.includes('.') ? rawBase.split('.').pop() : ''
  const extFromMime = mimeType && mimeType.includes('/') ? mimeType.split('/').pop() : ''
  const extension = (extFromName || extFromMime || 'jpg').replace(/[^a-z0-9]/ig, '') || 'jpg'
  const safeBase = baseWithoutExt.replace(/[^a-z0-9_-]+/ig, '-')
  const suffix = presetKey ? `-${presetKey}` : ''
  return `${safeBase}${suffix}.${extension}`
}

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const fallbackDirectDownload = (resolvedUrl, filename) => {
  const link = document.createElement('a')
  link.href = resolvedUrl
  link.target = '_blank'
  link.rel = 'noopener'
  if (filename) link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const downloadImage = async (payload, fallbackName, fallbackPreset) => {
  const { url, name, preset } = normalizeDownloadPayload(payload, fallbackName, fallbackPreset)
  if (!url) {
    console.warn('downloadImage: missing image url')
    return
  }

  try {
    const presetConfig = DOWNLOAD_PRESETS[preset] || DOWNLOAD_PRESETS.high
    let resolvedUrl = resolveAbsoluteUrl(url)
    let originalBlob = null

    try {
      originalBlob = await fetchImageBlob(resolvedUrl)
    } catch (fetchErr) {
      if (resolvedUrl.startsWith('http://')) {
        const httpsVariant = resolvedUrl.replace(/^http:/, 'https:')
        if (httpsVariant !== resolvedUrl) {
          resolvedUrl = httpsVariant
          originalBlob = await fetchImageBlob(resolvedUrl)
        } else {
          throw fetchErr
        }
      } else {
        throw fetchErr
      }
    }

    const processedBlob = await resizeBlobForPreset(originalBlob, presetConfig)
    const filename = buildFileName(name, presetConfig.key, processedBlob.type || originalBlob.type)
    triggerBlobDownload(processedBlob, filename)
  } catch (error) {
    console.error('Error downloading image:', error)
    try {
      const resolvedUrl = resolveAbsoluteUrl(url)
      const fallbackName = buildFileName(name, preset || 'high', 'image/jpeg')
      fallbackDirectDownload(resolvedUrl, fallbackName)
    } catch (fallbackErr) {
      console.error('Fallback download failed:', fallbackErr)
      window.alert('Unable to download this image right now. Please try again later.')
    }
  }
}
</script>

<style scoped>
.section-title{font-weight:600;margin-bottom:.5rem}
.text-small{font-size:.9rem}
.hero .slide-frame{position:relative;overflow:hidden;border-radius:8px}


/* HERO / SLIDER */
.slide-frame{
  position: relative;
  width: 100%;
  overflow: hidden;
}
.slide-frame::before{
  content: "";
  display: block;
  padding-top: 56.25%;
}
.slide-bg-blur{
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(20px) brightness(0.9);
  transform: scale(1.1);
}
.slide-img{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: .5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,.08);
}
.slide-img.fit-contain{ object-fit: contain; background: transparent; }
.slide-img.fit-cover{ object-fit: cover; object-position: top center; }
.video-preview-shell{
  position:absolute;
  inset:0;
  cursor:pointer;
}
.video-preview-play{
  position:absolute;
  left:50%;
  top:50%;
  transform:translate(-50%, -50%);
  width:64px;
  height:64px;
  border:0;
  border-radius:50%;
  background:rgba(14,165,233,.9);
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:22px;
  box-shadow:0 10px 24px rgba(2,6,23,.35);
}
.slide-player{
  position:absolute;
  inset:0;
  border-radius:.5rem;
  overflow:hidden;
  box-shadow:0 2px 10px rgba(0,0,0,.08);
  background:#000;
}
:deep(.slide-player .plyr){
  width:100%;
  height:100% !important;
  max-width:none;
  border-radius:.5rem;
  min-height:0;
}
:deep(.slide-player .plyr__video-wrapper){
  position:absolute;
  inset:0;
  height:100% !important;
  min-height:0;
  padding-bottom:0 !important;
}
:deep(.slide-player .plyr video){
  width:100%;
  height:100%;
  object-fit:cover;
  background:#000;
}
:deep(.slide-player .plyr__poster){
  background-size:cover !important;
  background-position:center !important;
}
:deep(.slide-player .plyr__video-embed){
  position:absolute;
  inset:0;
  height:100%;
}
:deep(.slide-player .plyr__video-embed iframe){
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
}
.slide-iframe-wrap{
  position:absolute;
  inset:0;
  border-radius:.5rem;
  overflow:hidden;
  background:#000;
}
.slide-iframe{
  width:100%;
  height:100%;
  border:0;
  display:block;
}


.nav{position:absolute;top:50%;transform:translateY(-50%);border:none;background:rgba(0,0,0,.4);color:#fff;width:36px;height:36px;border-radius:18px}
.nav.prev{left:8px}.nav.next{right:8px}

.thumbs{ display: flex; flex-wrap: nowrap; overflow-x: auto; gap: .5rem; padding-bottom: .25rem; }
.thumb-btn{ padding: 0; border: 2px solid transparent; background: transparent; border-radius: .35rem; line-height: 0; flex: 0 0 auto; }
.thumb-btn.active{ border-color: var(--brand-primary); }
.thumb-btn{ position:relative; }
.thumb{ width: 88px; height: 56px; object-fit: cover; border-radius: .35rem; display: block; box-shadow: 0 1px 6px rgba(0,0,0,.08); }
.thumb-play{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  pointer-events:none;
}
.thumb-play i{
  width:20px;
  height:20px;
  border-radius:50%;
  background:rgba(255, 0, 0, 0.92);
  color:#ffffff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:9px;
}

@media (max-width: 576px){
  .slide-img{height:220px}
}

.badge{display:inline-block;padding:.35em .6em;border-radius:6px}
.badge-success{background:#d4edda;color:#155724}
.badge-danger{background:#f8d7da;color:#721c24}
.badge-primary{background:#cfe2ff;color:#084298}
.badge-secondary{background:#ececec;color:#333}
.emoji-icon{display:inline-block;line-height:1;}

.map-card{
  border:1px solid #e5e7eb;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 8px 20px rgba(15,23,42,.08);
  background:#fff;
}
.map-card__header{
  padding:.75rem 1rem;
  font-weight:600;
  color:#0f172a;
  border-bottom:1px solid #f3f4f6;
  font-size:.95rem;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.5rem;
}
.map-card__header a{
  font-size:.85rem;
  color:#0d6efd;
  font-weight:500;
}
.map-frame{
  position:relative;
  padding-top:56.25%;
  background:#f1f5f9;
}
.map-frame__iframe{
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
  border:0;
}
</style>
